/**
 * usePractitionerClientView — Data hook for the Practitioner Client View screen.
 *
 * Fetches a specific client's analysis history, pattern trends, consent level,
 * and session prep notes via React Query. Uses client_analysis_outputs view
 * for consent-gated data access.
 *
 * Interface contract matches the spec in SPEC.md §3.
 *
 * Wire shape: the consent endpoint is validated against
 * `PractitionerConsentListDto`. The handler today returns ONLY the consent
 * rows (not the embedded `client_profile` + `analyses` payload this view
 * needs); those richer fields will land via a future server-side change.
 * Until then, the hook degrades to "no consent" when the endpoint cannot
 * supply the enriched payload — preserving the existing UX.
 *
 * Data-proxy migration status: FULLY MIGRATED (2026-04-02)
 * ────────────────────────────────────────────────────────
 * All queries now go through data-proxy.
 *
 * TODO (#2513): Client profile lookup is a cross-user query (practitioner
 * viewing a client's display_name). Data-proxy is owner-scoped so this
 * currently returns the practitioner's own profile, not the client's.
 * Needs a dedicated server-side RPC for cross-user profile resolution.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchJsonValidated,
  DtoValidationError,
} from '@dramadetector/api-client';
import {
  PractitionerConsentListDto,
} from '@dramadetector/shared/schemas';
import { getEdgeFunctionUrl } from '../lib/api-url';
import { getInitials, deriveTrend, formatMemberSince } from './practitioner-utils';

type Tier = 'free' | 'drama_plus' | 'dramax';
type NoteType = 'improving' | 'persistent' | 'suggested';
type Trend = 'improving' | 'stable' | 'worsening';

export interface PrepNote { type: NoteType; text: string }

export interface PatternTrend {
  id: string; name: string; score: number; trend: Trend; maxScore?: number;
}

export interface AnalysisHistoryItem {
  id: string; date: string; patternLabel: string; score: number;
  delta: number | null; isNew: boolean;
}

export interface ChatMessage {
  id: string; senderId: string; senderInitials: string;
  senderGradientIndex: number; text: string; timestamp: string; isSent: boolean;
}

export interface ClientConsent {
  canViewHistory: boolean; canViewOriginalMessage: boolean;
  canChat: boolean; requiresApprovalForResults: boolean;
}

export interface UsePractitionerClientViewReturn {
  clientName: string; clientInitials: string;
  consentLevel: 'scores_and_patterns' | 'full_history' | 'original_text';
  memberSince: string; analysisCount: number; tierName: Tier;
  sessionPrepNotes: PrepNote[]; patternTrends: PatternTrend[];
  analysisHistory: AnalysisHistoryItem[]; chatMessages: ChatMessage[];
  consent: ClientConsent; isLoading: boolean; error: Error | null;
}

interface ClientAnalysisOutputRow {
  id: string;
  score: number | null;
  category: string | null;
  created_at: string;
}

// Query key factory
export const practitionerClientViewKeys = {
  all: ['practitioner-client-view'] as const,
  client: (practitionerId: string | null, clientId: string | undefined) =>
    [...practitionerClientViewKeys.all, practitionerId, clientId] as const,
};

function deriveConsentLevel(c: ClientConsent) {
  if (c.canViewOriginalMessage) return 'original_text' as const;
  if (c.canViewHistory) return 'full_history' as const;
  return 'scores_and_patterns' as const;
}

function generatePrepNotes(
  analyses: Array<{ category: string | null; score: number | null }>
): PrepNote[] {
  if (analyses.length < 2) return [];
  const byCategory = new Map<string, number[]>();
  for (const a of analyses) {
    const cat = a.category ?? 'unknown';
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat)!.push(a.score ?? 0);
  }
  const notes: PrepNote[] = [];
  for (const [cat, scores] of Array.from(byCategory.entries())) {
    if (scores.length < 2) continue;
    const trend = deriveTrend(scores);
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
    if (trend === 'improving') {
      const pct = Math.round(
        Math.abs(((scores[scores.length - 1] - scores[0]) / scores[0]) * 100)
      );
      notes.push({ type: 'improving', text: `${cat} down ${pct}% over ${scores.length} analyses` });
    } else if (trend === 'stable' && avg > 5) {
      notes.push({ type: 'persistent', text: `${cat} remains elevated (${avg.toFixed(1)} avg)` });
    }
  }
  return notes.slice(0, 5);
}

const EMPTY_CONSENT: ClientConsent = {
  canViewHistory: false, canViewOriginalMessage: false,
  canChat: false, requiresApprovalForResults: false,
};

export function usePractitionerClientView(
  clientId?: string
): UsePractitionerClientViewReturn {
  const { user, accessToken } = useAuth();
  const practitionerId = user?.id ?? null;

  const { data, isLoading, error } = useQuery({
    queryKey: practitionerClientViewKeys.client(practitionerId, clientId),
    queryFn: async () => {
      if (!practitionerId || !clientId) return null;

      // Fetch consent data for this practitioner-client pair.
      // The handler today emits a `PractitionerConsentListDto` (array). The
      // enriched `client_profile` + `analyses` payload that this view used
      // to read is not yet returned by the server — until then we surface
      // "no consent" so the UI gracefully degrades.
      // Pass clientId — the handler scopes by practitioner_id = current_user.id
      // automatically via applyOwnerFilter when clientId is present.
      const consentUrl = getEdgeFunctionUrl('get-practitioner-consent') +
        '?clientId=' + encodeURIComponent(clientId);

      try {
        const list = await fetchJsonValidated(PractitionerConsentListDto, consentUrl);
        const row = list[0];
        if (!row) return { noConsent: true as const };
        return {
          noConsent: false as const,
          consent: {
            can_view_history: row.can_view_history,
            can_view_original_message: row.can_view_original_message,
            can_chat: row.can_chat,
            requires_approval_for_results: row.requires_approval_for_results,
          },
          // Enriched fields not yet returned by the worker (NocoDB #2513).
          clientProfile: null as null | {
            display_name?: string;
            created_at?: string;
            subscription_tier?: string;
          },
          analyses: [] as ClientAnalysisOutputRow[],
        };
      } catch (err) {
        if (err instanceof DtoValidationError) {
          return { noConsent: true as const };
        }
        throw err;
      }
    },
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 15,
    enabled: !!accessToken && !!practitionerId && !!clientId,
    refetchOnMount: true,
  });

  // Memoize all data transformations (pattern trends, history, prep notes)
  // to avoid re-computing on every render when data hasn't changed.
  return useMemo((): UsePractitionerClientViewReturn => {
    if (!data || data.noConsent || isLoading) {
      return {
        clientName: '', clientInitials: '', consentLevel: 'scores_and_patterns',
        memberSince: '', analysisCount: 0, tierName: 'free', sessionPrepNotes: [],
        patternTrends: [], analysisHistory: [], chatMessages: [],
        consent: EMPTY_CONSENT, isLoading: isLoading && !!accessToken, error: error as Error | null,
      };
    }

    const { consent, clientProfile, analyses } = data;
    const clientName = clientProfile?.display_name ?? 'Client';
    const consentObj: ClientConsent = {
      canViewHistory: consent.can_view_history,
      canViewOriginalMessage: consent.can_view_original_message,
      canChat: consent.can_chat,
      requiresApprovalForResults: consent.requires_approval_for_results,
    };

    // Build pattern trends
    const byCategory = new Map<string, number[]>();
    for (const a of analyses) {
      const cat = a.category ?? 'unknown';
      if (!byCategory.has(cat)) byCategory.set(cat, []);
      byCategory.get(cat)!.push(a.score ?? 0);
    }
    const patternTrends: PatternTrend[] = Array.from(byCategory.entries()).map(
      ([cat, scores]) => ({
        id: cat, name: cat.charAt(0).toUpperCase() + cat.slice(1),
        score: scores[0], trend: deriveTrend(scores), maxScore: Math.max(...scores),
      })
    );

    // Build history items
    const analysisHistory: AnalysisHistoryItem[] = analyses.map((a, idx) => {
      const prev = idx < analyses.length - 1 ? analyses[idx + 1].score : null;
      return {
        id: a.id,
        date: new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        patternLabel: a.category ? a.category.charAt(0).toUpperCase() + a.category.slice(1) : 'Unknown',
        score: a.score ?? 0,
        delta: prev != null && a.score != null ? Math.round((a.score - prev) * 10) / 10 : null,
        isNew: idx === 0 && analyses.length > 1,
      };
    });

    return {
      clientName, clientInitials: getInitials(clientName),
      consentLevel: deriveConsentLevel(consentObj),
      memberSince: clientProfile?.created_at ? formatMemberSince(clientProfile.created_at) : '',
      analysisCount: analyses.length,
      tierName: (clientProfile?.subscription_tier as Tier) ?? 'free',
      sessionPrepNotes: generatePrepNotes(analyses), patternTrends, analysisHistory,
      chatMessages: [], // Chat requires Sendbird integration (future work)
      consent: consentObj, isLoading: false, error: error as Error | null,
    };
  }, [data, isLoading, accessToken, error]);
}
